import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { Spin } from "antd";
import Instance from "../../AxiosConfig";
import "./dashboard.css";

const YearlyGrowthGraph = () => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState("");
  const [manualMax, setManualMax] = useState("");
  const [chartData, setChartData] = useState([]);

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  useEffect(() => {
    setSelectedYear(new Date().getFullYear().toString());
  }, []);

  // ==========================
  // Fetch Chart Data
  // ==========================
  const fetchChartData = async () => {
    if (!selectedYear) return;

    setLoading(true);

    try {
      const response = await Instance.get(
        "/dashboard/monthly-closure-amount",
        {
          params: {
            year: selectedYear,
          },
        }
      );

      const apiData = response.data?.data || [];

      const values = new Array(12).fill(0);

      apiData.forEach((item) => {
        values[item.month - 1] = Number(item.totalClosure || 0);
      });

      setChartData(values);
    } catch (error) {
      console.error(error);
      setChartData(new Array(12).fill(0));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [selectedYear]);

  // ==========================
  // Render Chart
  // ==========================
  useLayoutEffect(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext("2d");

    const maxValue = Math.max(...chartData, 0);

    const DEFAULT_Y_MAX = 1000000;

    let yMax;

    if (manualMax) {
      yMax = Number(manualMax);
    } else {
      const calculatedMax = Math.ceil(maxValue * 1.1);
      yMax = Math.max(DEFAULT_Y_MAX, calculatedMax);
    }

    const stepSize = Math.ceil(yMax / 5);

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    chartInstanceRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: monthNames,
        datasets: [
          {
            label: "Monthly Closure",
            data: chartData,
            backgroundColor: "rgba(19,106,213,0.75)",
            borderColor: "#136AD5",
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
            barThickness: window.innerWidth < 768 ? 20 : 28,
            maxBarThickness: window.innerWidth < 768 ? 20 : 28,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: "#64748B",
              autoSkip: false,
            },
          },
          y: {
            beginAtZero: true,
            max: yMax,
            ticks: {
              stepSize,
              color: "#9CA3AF",
              callback: (value) =>
                Number(value).toLocaleString("en-IN"),
            },
            grid: {
              color: "rgba(0,0,0,0.08)",
            },
          },
        },
      },
    });

    return () => {
      chartInstanceRef.current?.destroy();
    };
  }, [chartData, manualMax]);

  return (
    <div className="chart-card mt-4">
      <div className="chart-header">
        <h2 className="chart-title">Yearly Growth Graph</h2>

        <div className="chart-dropdowns">
          <select
            className="chart-dropdown"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {Array.from({ length: 2050 - 2025 + 1 }, (_, i) => 2025 + i).map(
              (year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              )
            )}
          </select>

          <input
            type="number"
            className="chart-dropdown"
            placeholder="Enter Max Amount"
            value={manualMax}
            onChange={(e) => setManualMax(e.target.value)}
          />
        </div>
      </div>

      <div
        className="chart-body"
        style={{
          position: "relative",
          minHeight: 300,
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "rgba(255,255,255,.6)",
              zIndex: 100,
            }}
          >
            <Spin size="large" />
          </div>
        )}

        <canvas ref={chartRef} />
      </div>
    </div>
  );
};

export default YearlyGrowthGraph;